// refreshTokenMiddleware.js
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { store } from './store';
import { logoutUser } from '../actions/user';
import BASE_URL from "../constants/Api";

// Create an axios instance
const axiosInstance = axios.create();

// Request interceptor to add authorization header
axiosInstance.interceptors.request.use(config => {
    const token = localStorage.getItem('accessToken');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
}, error => Promise.reject(error));

// Function to check if token refresh is needed
const shouldRefreshToken = error => error.response.status === 502;

// Logic to refresh token
const refreshAuthLogic = async failedRequest => {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
        handleLogout();
        return Promise.reject(new Error('No refresh token found'));
    }

    try {
        const response = await axios.post(`${BASE_URL}/users/refresh-token`, { refresh_token: refreshToken }, {
            headers: { Authorization: `Bearer ${refreshToken}` }
        });

        const { access_token, refresh_token, maintenance } = response.data.respons;
        if (maintenance === 1) {
            handleLogout();
            return Promise.reject(new Error('Maintenance'));
        }
        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        failedRequest.response.config.headers['Authorization'] = `Bearer ${access_token}`;
        return Promise.resolve();
    } catch (error) {
        console.error('Failed to refresh token', error);
        handleLogout();
        return Promise.reject(error);
    }
};

// Function to handle logout
const handleLogout = () => {
    const remember = localStorage.getItem('remember') === 'true';
    const loginId = localStorage.getItem('loginId');

    localStorage.clear();

    if (remember) {
        localStorage.setItem('remember', 'true');
        if (loginId) localStorage.setItem('loginId', loginId);
    }

    store.dispatch(logoutUser());
    window.location.href = '/login';
};

// Attach refresh logic to axios instance
createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic, { shouldRefresh: shouldRefreshToken });

// Response interceptor to handle token expiration and retry logic
axiosInstance.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        if (error.response.status === 501 && originalRequest.url === `${BASE_URL}/users/refresh-token`) {
            handleLogout();
            return Promise.reject(error);
        }

        if (error.response.status !== 502 || originalRequest._retry) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
            await refreshAuthLogic(originalRequest);
            originalRequest.headers['Authorization'] = `Bearer ${localStorage.getItem('accessToken')}`;
            return axiosInstance(originalRequest);
        } catch (err) {
            console.error('Failed to retry request after refreshing token', err);
            handleLogout();
            return Promise.reject(err);
        }
    }
);

export default axiosInstance;
